import React from "react";
import { Link } from "gatsby";

import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import CareTeamMap from "../components/CareTeamMap";
import TrustpilotInline from "../components/TrustpilotInline";

import "../../scss/main.scss";

const imagePath = "/assets/marketing-site/images";

const supportAreas = [
	[
		"livein-icon-01.png",
		"Conversation and shared interests",
		"Time for a proper chat, favourite programmes, music, games, hobbies or simply enjoying a cup of tea together.",
	],
	[
		"livein-icon-07.png",
		"Meals and shopping",
		"Planning meals, shopping together and preparing food that feels familiar, enjoyable and right for the person.",
	],
	[
		"livein-icon-02.png",
		"Appointments and outings",
		"Company for appointments, errands, visits, walks and the local places that keep someone connected to their community.",
	],
	[
		"livein-icon-06.png",
		"Everyday home routines",
		"Light housekeeping, laundry, post and the small practical jobs that help the day feel calm and manageable.",
	],
	[
		"livein-icon-04.png",
		"Prompts and reassurance",
		"Gentle reminders around planned routines, hydration or medication, always in line with the agreed care plan.",
	],
	[
		"livein-icon-03.png",
		"Family connection",
		"A familiar professional presence who notices changes and keeps the wider Edyn care team informed.",
	],
];

const helpfulSituations = [
	[
		"Days have become quieter",
		"Regular company can bring conversation, purpose and something to look forward to without filling every moment of the day.",
	],
	[
		"Confidence has reduced",
		"A trusted person alongside can make shopping, appointments, walks and favourite activities feel possible again.",
	],
	[
		"Routines are becoming harder",
		"Practical help with meals, errands and light household tasks can make home life feel less overwhelming.",
	],
	[
		"Family cannot always be there",
		"A consistent carer and managed care team can provide reassurance while keeping relatives appropriately informed.",
	],
	[
		"Recovery needs a gentle bridge",
		"After illness or a hospital stay, companionship and practical support can help someone settle back into life at home.",
	],
	[
		"A family carer needs a break",
		"Planned support can protect important family relationships and give everyone more sustainable time and space.",
	],
];

const faqs = [
	[
		"Is companionship care only for people who live alone?",
		"No. It can help anyone who would value regular company, practical support or more confidence in daily life. Some people live alone, while others live with a partner or family member who cannot always provide the support needed.",
	],
	[
		"Can companionship care include personal care?",
		"Yes, if personal care is identified in the assessment and included in the care plan. Companionship may sit alongside help with washing, dressing, mobility or medication, so the arrangement can adapt without losing the relationship at its centre.",
	],
	[
		"Can a carer come to appointments or help someone get out?",
		"Yes. A carer can accompany someone to appointments, shops, social visits and local activities. If driving is needed, Edyn will discuss the practical requirements and look for a suitable carer match.",
	],
	[
		"Is this hourly care or live-in care?",
		"This page describes the companionship and day-to-day support that can form part of an Edyn care plan. Edyn will explain the available arrangement after understanding how much support is needed and when it is needed.",
	],
	[
		"How do you choose the right carer?",
		"Skills and safe recruitment checks matter, but shared interests, communication style and personality matter too. Edyn discusses what a good day looks like and uses that picture to guide the carer match.",
	],
	[
		"What happens if needs change?",
		"The Care Manager reviews the care plan and can add, reduce or change support as circumstances develop. Families do not have to start the search again or manage changes alone.",
	],
];

export default function CompanionshipCare() {
	return (
		<div className="container edyn-redesign-shell">
			<Seo
				title="Companionship Care and Day-to-Day Support | Edyn Care"
				description="Companionship care and practical day-to-day support at home, shaped around familiar routines, independence and the life someone wants to keep living."
				path="/companionship-care/"
				image="https://www.edyn.care/assets/marketing-site/images/live-in-image-01.jpg"
				service={{
					name: "Companionship care and day-to-day support",
					serviceType: "Companionship care at home",
				}}
				breadcrumbs={[
					{ name: "Home", path: "/" },
					{ name: "Companionship care", path: "/companionship-care/" },
				]}
			/>
			<Header />

			<main id="main-content" className="edyn-companionship">
				<section className="companionship-hero">
					<div className="site-shell companionship-hero__grid">
						<div className="companionship-hero__copy">
							<p className="eyebrow eyebrow--light">
								Companionship and day-to-day support
							</p>
							<h1>A little help can keep everyday life feeling like yours.</h1>
							<p className="companionship-hero__lead">
								A professional carer brings good company, practical help and
								steady reassurance, while protecting the routines, choices and
								independence that make home feel like home.
							</p>
							<div className="button-row">
								<Link className="button button--cream" to="/care-consultation/">
									Talk to a Care Advisor
								</Link>
								<a
									className="button button--text-light"
									href="#everyday-support"
								>
									See everyday support <span aria-hidden="true">&#8595;</span>
								</a>
							</div>
							<TrustpilotInline className="companionship-hero__trustpilot" />
						</div>

						<div className="companionship-hero__visual">
							<img
								src={`${imagePath}/live-in-image-01.jpg`}
								alt="A professional carer and an older woman laughing together at home"
								width="1327"
								height="1115"
							/>
							<div className="companionship-hero__note">
								<span aria-hidden="true">&#10003;</span>
								<p>
									<strong>Company that feels natural.</strong>
									Support that keeps life moving.
								</p>
							</div>
						</div>
					</div>
				</section>

				<section
					className="companionship-proof"
					aria-label="Service highlights"
				>
					<div className="site-shell companionship-proof__grid">
						<p>
							<strong>One-to-one</strong>
							<span>Time shaped around one person</span>
						</p>
						<p>
							<strong>Familiar routines</strong>
							<span>Support that fits the way they live</span>
						</p>
						<p>
							<strong>Fully managed</strong>
							<span>A care team stays involved</span>
						</p>
					</div>
				</section>

				<section className="companionship-intro section-space">
					<div className="site-shell companionship-intro__grid">
						<div>
							<p className="eyebrow">More than company</p>
							<h2>Someone to share the day with. Support to keep it moving.</h2>
						</div>
						<div className="companionship-intro__copy">
							<p className="section-lead">
								Companionship care is personal because a good day means
								something different to everyone.
							</p>
							<p>
								It might mean cooking lunch together, getting to a regular club,
								walking the dog or having another person nearby while daily
								tasks get done. The aim is not to organise someone&apos;s life
								for them. It is to make more of their own life possible.
							</p>
						</div>
					</div>
				</section>

				<section
					id="everyday-support"
					className="companionship-support section-space"
				>
					<div className="site-shell">
						<div className="section-heading section-heading--split">
							<div>
								<p className="eyebrow">Day-to-day support</p>
								<h2>Small things, thoughtfully done.</h2>
							</div>
							<p>
								Every care plan is individual. These are common ways a companion
								carer can help someone stay connected, confident and comfortable
								at home.
							</p>
						</div>

						<div className="companionship-support__grid">
							{supportAreas.map(([icon, title, description], index) => (
								<article key={title}>
									<span className="companionship-support__number">
										{String(index + 1).padStart(2, "0")}
									</span>
									<span className="companionship-support__icon" aria-hidden="true">
										<img src={`${imagePath}/${icon}`} alt="" loading="lazy" />
									</span>
									<div className="companionship-support__copy">
										<h3>{title}</h3>
										<p>{description}</p>
									</div>
								</article>
							))}
						</div>
					</div>
				</section>

				<section className="companionship-life section-space">
					<div className="site-shell companionship-life__grid">
						<div className="companionship-life__visual">
							<picture>
								<source srcSet={`${imagePath}/livein-imageclus-09-640.webp 640w, ${imagePath}/livein-imageclus-09-1200.webp 1200w`} sizes="(max-width: 700px) 100vw, 50vw" type="image/webp" />
								<img src={`${imagePath}/livein-imageclus-09-1200.webp`} alt="A carer and an older woman enjoying time together" width="1200" height="1836" loading="lazy" />
							</picture>
							<div className="companionship-life__note">
								<span aria-hidden="true">&#10003;</span>
								<p>
									<strong>Company where wanted.</strong>
									Independence everywhere possible.
								</p>
							</div>
						</div>

						<div className="companionship-life__copy">
							<p className="eyebrow">Care around the person</p>
							<h2>The relationship matters as much as the routine.</h2>
							<ul>
								<li>
									<strong>Start with the person</strong>
									<span>
										Interests, preferences and boundaries shape the plan.
									</span>
								</li>
								<li>
									<strong>Choose for compatibility</strong>
									<span>
										Personality and shared interests inform the carer match.
									</span>
								</li>
								<li>
									<strong>Keep reviewing</strong>
									<span>
										The care team adapts support when life or needs change.
									</span>
								</li>
							</ul>
						</div>
					</div>
				</section>

				<section className="companionship-when section-space section-dark">
					<div className="site-shell">
						<div className="section-heading section-heading--split">
							<div>
								<p className="eyebrow eyebrow--light">When it can help</p>
								<h2>Support can begin before there is a crisis.</h2>
							</div>
							<p>
								There is no single moment when companionship becomes necessary.
								Often it starts with small changes that make daily life
								narrower, less confident or harder to sustain.
							</p>
						</div>

						<div className="companionship-when__grid">
							{helpfulSituations.map(([title, description], index) => (
								<article key={title}>
									<span>{String(index + 1).padStart(2, "0")}</span>
									<h3>{title}</h3>
									<p>{description}</p>
								</article>
							))}
						</div>
					</div>
				</section>

				<section className="companionship-plan section-space">
					<div className="site-shell companionship-plan__grid">
						<div className="companionship-plan__heading">
							<p className="eyebrow">One joined-up care plan</p>
							<h2>
								Companionship and personal care do not have to be separate.
							</h2>
							<p>
								The right arrangement can begin with lighter day-to-day support
								and change over time, without losing the familiar relationship
								at its centre.
							</p>
						</div>

						<div className="companionship-plan__cards">
							<article>
								<span>Companionship</span>
								<h3>Life, routines and connection</h3>
								<ul>
									<li>Conversation and shared interests</li>
									<li>Meals, shopping and light household tasks</li>
									<li>Appointments, outings and confidence</li>
								</ul>
							</article>
							<article className="is-personal-care">
								<span>Personal care, when needed</span>
								<h3>Discreet practical support</h3>
								<ul>
									<li>Washing, dressing and continence</li>
									<li>Mobility and safe medication support</li>
									<li>Needs assessed, planned and reviewed by Edyn</li>
								</ul>
							</article>
						</div>
					</div>
				</section>

				<section className="home-team companionship-managed-overview section-space section-dark">
					<div className="site-shell home-team__grid">
						<div className="home-team__copy">
							<p className="eyebrow eyebrow--light">Fully managed by Edyn</p>
							<h2>A familiar carer, with a whole care team behind them.</h2>
							<p>
								Edyn assesses and plans care, supports the professional carer
								and stays close to the family. Care coordination and thoughtful
								technology help important details stay connected.
							</p>
							<Link className="button button--cream" to="/approach/">
								See how Edyn works
							</Link>
						</div>
						<CareTeamMap />
					</div>
				</section>

				<section className="companionship-pricing section-space">
					<div className="site-shell companionship-pricing__grid">
						<div>
							<p className="eyebrow">Clear costs</p>
							<h2>Understand the arrangement before making a decision.</h2>
						</div>
						<div>
							<p>
								Cost depends on how much support is needed and the shape of the
								care plan. A Care Advisor can explain the available options,
								likely cost and routes to paying for care without pressure.
							</p>
							<div className="button-row">
								<Link className="button button--primary" to="/pricing/">
									See pricing
								</Link>
								<Link className="button button--text" to="/care-consultation/">
									Discuss your situation <span aria-hidden="true">&#8594;</span>
								</Link>
							</div>
						</div>
					</div>
				</section>

				<section className="companionship-faq section-space">
					<div className="site-shell companionship-faq__grid">
						<div>
							<p className="eyebrow">Common questions</p>
							<h2>Useful answers before you begin.</h2>
							<p>
								A conversation with a Care Advisor will give you answers
								grounded in the person, their home and what is changing now.
							</p>
						</div>
						<div className="companionship-faq__list">
							{faqs.map(([question, answer]) => (
								<details key={question}>
									<summary>{question}</summary>
									<p>{answer}</p>
								</details>
							))}
						</div>
					</div>
				</section>
			</main>

			<Footer />
		</div>
	);
}
